import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';
import { debounce } from '@theme/utilities';

/**
 * Kasım kampanyası sepet ilerleme çubuğu (DEV-550).
 *
 * Tutarlar Liquid'de hesaplanır (snippets/cart-progress-bar.liquid). Sepet çekmecesi
 * ve sepet sayfası değişiklikte zaten Section Rendering API ile yeniden çizildiği için
 * çubuk çoğu durumda onlarla birlikte güncellenir. Bu bileşen iki boşluğu kapatır:
 * - Bölüm HTML'i taşımayan sepet olaylarında (StandardEvents.cartLinesUpdate /
 *   cartDiscountUpdate) çubuğu
 *   sections/cart-progress-bar.liquid üzerinden yeniden çeker (/cart.js değil).
 * - Önbellekten gelen bir sayfada tarih penceresi dışındaysa çubuğu gizler.
 */

const SECTION_ID = 'cart-progress-bar';
const TIME_ZONE = 'Europe/Istanbul';

/**
 * Sayfadaki bütün çubukları tek istekle yeniler.
 */
const refreshBars = debounce(async () => {
  const url = new URL(Theme.routes.cart_url, window.location.origin);
  url.searchParams.set('sections', SECTION_ID);

  try {
    const response = await fetch(url);
    if (!response.ok) return;

    /** @type {Record<string, string | null>} */
    const sections = await response.json();
    const html = sections[SECTION_ID];
    const freshBar = html
      ? new DOMParser().parseFromString(html, 'text/html').querySelector('cart-progress-bar')
      : null;

    for (const bar of document.querySelectorAll('cart-progress-bar')) {
      if (!(bar instanceof CartProgressBar)) continue;

      if (freshBar) {
        morph(bar, freshBar.cloneNode(true));
        // morph `hidden` özniteliğini kaldırmıyor; önceki gizlemeyi elle geri al
        bar.hidden = false;
        bar.applyDateWindow();
      } else {
        // Sepet boşaldı ya da kampanya penceresi kapandı
        bar.hidden = true;
      }
    }
  } catch (error) {
    console.error(error);
  }
}, 150);

/**
 * @returns {number} Bugünün tarihi İstanbul saatine göre YYYYMMDD olarak
 */
function todayInStoreTimeZone() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

  return Number(parts.replace(/\D/g, ''));
}

class CartProgressBar extends HTMLElement {
  connectedCallback() {
    this.applyDateWindow();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartUpdate);
  }

  /**
   * Sunucu tarih penceresini zaten uygular; bu yalnızca önbellekten gelen HTML için.
   */
  applyDateWindow() {
    if (this.hasAttribute('data-design-mode')) return;

    const start = Number(this.dataset.start) || 0;
    const end = Number(this.dataset.end) || 0;
    const today = todayInStoreTimeZone();

    if ((start && today < start) || (end && today > end)) {
      this.hidden = true;
    }
  }

  /**
   * Dwell 4.x sepet olayları sonucu `event.promise` ile verir (istek bitince çözülür).
   * @param {Event & { promise?: Promise<{ detail?: { sections?: Record<string, string> } }> }} event
   */
  #handleCartUpdate = (event) => {
    if (!event.promise) {
      refreshBars();
      return;
    }

    event.promise
      .then((result) => {
        // Çubuğu taşıyan bölümün yeni HTML'i olayla geldiyse cart-items-component onu zaten morph ediyor
        const sectionId = this.closest('cart-items-component')?.getAttribute('data-section-id');
        if (sectionId && result?.detail?.sections?.[sectionId]) return;

        refreshBars();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') refreshBars();
      });
  };
}

if (!customElements.get('cart-progress-bar')) {
  customElements.define('cart-progress-bar', CartProgressBar);
}
